import type { CreatorStatistic } from '@api'
import type { ColumnDef } from '@tanstack/react-table'

import IconChevronRight from '@/assets/icons/icon-chevron-right.svg'
import { Button } from '@/components/ui/button.tsx'
import { getSalary } from '@/modules/accounting/utils.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { roundAmount } from '@/utils/number.ts'

export const getCreatorColumns = ({
  showCents,
  onSetSalary,
}: {
  showCents: boolean
  onSetSalary: (item: CreatorStatistic) => void
}): ColumnDef<CreatorStatistic>[] => [
  {
    accessorKey: 'creatorName',
    header: 'Creator',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <CreatorAvatar
          name={row.original.creatorName ?? ''}
          avatarSrc={row.original.avatar}
          size={40}
        />
        {row.original.creatorName}
      </div>
    ),
  },
  {
    accessorKey: 'revenue',
    header: 'Total Revenue',
    sortingFn: 'auto',
    cell: ({ row }) => roundAmount(row.original.revenue, showCents),
  },
  {
    accessorKey: 'agencyProfit',
    header: 'Creator Salary',
    sortingFn: (a, b) => getSalary(a.original) - getSalary(b.original),
    cell: ({ row }) => {
      const salary = getSalary(row.original)

      return roundAmount(salary, showCents) ?? '—'
    },
  },
  {
    accessorKey: 'cut',
    header: 'My Cut',
    sortingFn: 'auto',
    enableSorting: true,
    cell: ({ row }) => roundAmount(row.original.agencyProfit, showCents),
  },
  {
    accessorKey: 'fixedSalary',
    header: 'Fixed Salary',
    sortingFn: (a, b) => {
      return (
        (a.original.compensation?.fixedSalary ?? 0) -
        (b.original.compensation?.fixedSalary ?? 0)
      )
    },
    cell: ({ row }) => (
      <span data-testid="fixed-salary-value">
        {roundAmount(row.original.compensation?.fixedSalary, showCents)}
      </span>
    ),
  },
  {
    accessorKey: 'cutOfRevenue',
    header: 'Percentage Cut of Revenue',
    sortingFn: (a, b) => {
      return (
        (a.original.compensation?.cutOfRevenuePercentage ?? 0) -
        (b.original.compensation?.cutOfRevenuePercentage ?? 0)
      )
    },
    cell: ({ row }) => (
      <span data-testid="cut-of-revenue-value">{`${row.original.compensation?.cutOfRevenuePercentage ?? 0}%`}</span>
    ),
  },
  {
    id: 'actions',
    // @ts-ignore
    header: <div className="w-full text-right">Action</div>,
    cell: function ActionCell({ row }) {
      return (
        <div className="flex justify-end">
          <Button
            onClick={() => onSetSalary(row.original)}
            variant="ghost"
            className="p-0"
            testId="set-salary-button"
          >
            Set Salary <IconChevronRight />
          </Button>
        </div>
      )
    },
  },
]
